<script setup lang="ts">
const emit = defineEmits(['open', 'close', 'cancel'])

const props = defineProps<{
  title?: string | undefined
  size?: 'default' | 'large'
  padding?: 'default' | 'none'
  preventDismissing?: boolean
  disableScroll?: boolean
  disableSwipeToClose?: boolean
  centerMobile?: boolean
  disableMobileHeight?: boolean
}>()

const isOpen = ref(false)

const element: Ref<HTMLIonModalElement | undefined> = ref()
const toolbarTitle: Ref<HTMLElement | undefined> = ref()
const contentTitle: Ref<HTMLElement | undefined> = ref()
const content: Ref<HTMLDivElement | undefined> = ref()

function open() {
  isOpen.value = true
}

function close(cancelled: boolean = false) {
  isOpen.value = false
  emit('close')
  if (cancelled) {
    emit('cancel')
  }
}

const resizeObserver = new ResizeObserver(() => {
  requestAnimationFrame(() => {
    try {
      resize()
    } catch (ex) {}
  })
})

const intersectionObserver = new IntersectionObserver((entries) => {
  toolbarTitle.value &&
    toolbarTitle.value.classList.toggle(
      'os-dialog__toolbar__title--visible',
      false && isOpen.value && entries[0].intersectionRatio <= 0,
    )
})

// Remove the && false to have ios-like modals
const page =
  isApp() && false
    ? computed(() => element.value?.closest('.ion-page') as HTMLElement)
    : ref(null)

const height = ref(0)

function resize() {
  if (content.value) {
    content.value && resizeObserver.observe(content.value)
    height.value =
      (content.value?.offsetHeight ?? 0) +
      (props.preventDismissing ? 0 : props.title?.trim() == '' ? 0 : 14)
  }

  setTimeout(() => {
    contentTitle.value && intersectionObserver.observe(contentTitle.value)
  }, 500)
}

resize()

defineExpose({
  open,
  close,
  isOpen,
})
</script>

<template>
  <div ref="element">
    <ion-modal
      :isOpen="isOpen"
      @didDismiss="(e) => close(e.detail?.role === 'backdrop')"
      @willPresent="resize"
      @didPresent="emit('open')"
      :can-dismiss="!isOpen || (!preventDismissing && !disableSwipeToClose)"
      :presenting-element="page"
      class="os-dialog"
      :class="`os-dialog--size-${size ?? 'default'} os-dialog--padding-${padding ?? 'default'}${centerMobile ? ' os-dialog--center-mobile' : ''}${!disableMobileHeight ? ' os-dialog--fullscreen-mobile' : ''}`"
      :style="'--calculatedHeight: ' + height + 'px'"
      aria-label="{{ title }}"
    >
      <ion-header
        v-if="!preventDismissing"
        style="margin-bottom: -55px; pointer-events: none"
      >
        <ion-toolbar>
          <h2 v-if="title" class="os-dialog__toolbar__title" ref="toolbarTitle">
            {{ title }}
          </h2>

          <ion-buttons slot="end">
            <ion-button
              fill="solid"
              @click="close(true)"
              :title="$t('label.close')"
              style="pointer-events: all"
            >
              <ion-icon :icon="ioniconsCloseOutline" slot="icon-only" />
            </ion-button>
          </ion-buttons>
        </ion-toolbar>
      </ion-header>

      <ion-content
        :scroll-y="!props.disableScroll"
        :class="{ 'os-dialog--no-scroll': props.disableScroll }"
      >
        <div>
          <div class="os-dialog__content" ref="content">
            <h1
              v-if="title"
              class="os-dialog__content__title"
              ref="contentTitle"
              id="modal-title"
            >
              {{ title }}
            </h1>
            <slot />
          </div>
        </div>
      </ion-content>
    </ion-modal>
  </div>
</template>

<style scoped>
.os-dialog {
  background-color: var(--default-transparent);
}

.os-dialog__content {
  position: relative;
  padding: 2rem 2rem 1rem 2rem !important;
}

.os-dialog--padding-none .os-dialog__content {
  padding: 0 !important;
}

.os-dialog__toolbar__title {
  opacity: 0;
  transition: opacity 300ms ease;
}

.os-dialog__toolbar__title--visible {
  opacity: 1;
}

.os-dialog__content__title {
  margin-bottom: 2rem;
}

.os-dialog--fullscreen-mobile {
  --width: 100% !important;
  --height: 85% !important;
  --max-height: 100% !important;
}

.os-dialog--fullscreen-mobile .os-dialog__content {
  margin-top: 3rem;
  max-height: 70%;
  box-sizing: border-box;
}

@media (min-width: 769px) {
  .os-dialog--fullscreen-mobile {
    --max-height: var(--calculatedHeight) !important;
  }

  .os-dialog--fullscreen-mobile .os-dialog__content {
    margin-top: 0;
  }
}

.os-dialog--center-mobile .os-dialog__content {
  font-size: 0.9rem;
}

@media (min-width: 769px) {
  .os-dialog--center-mobile .os-dialog__content {
    font-size: 1rem;
  }
}

.os-dialog:not(.os-dialog--size-large) {
  --max-height: min(var(--height), var(--calculatedHeight));
}

/** Disable scroll for img slide */
.os-dialog--no-scroll {
  overflow: hidden !important;
  height: 100%;
}

@media only screen and (min-width: 768px) and (min-height: 600px) {
  .os-dialog--size-large {
    --max-width: 850px !important;
    --max-height: 632px !important;
    --max-height: 577px !important;
  }
}

@media only screen and (min-width: 1024px) and (min-height: 768px) {
  .os-dialog--size-large {
    --max-width: 1024px !important;
    --max-height: 750px !important;
    --max-height: 695px !important;
  }
}

@media only screen and (min-width: 1264px) and (min-height: 1024px) {
  .os-dialog--size-large {
    --max-width: 1270px !important;
    --max-height: 916px !important;
    --max-height: 862px !important;
  }
}
</style>

<style>
ion-toolbar {
  --border-width: 0 !important;
}
</style>
